import { supabase } from './supabase';
import { apiFetch } from './api';
import type { UserSubscription, SubscriptionDisplayData, TierId } from '@/types/subscription';
import { getTierConfig, isPaidStatus } from '@/types/subscription';
import { getPlanBenefits, STRIPE_PRODUCTS, getPlanFromPriceId } from './stripe-config';
import { loadSubscriptionPrices } from './subscription-prices';
import { getTierFromSubscription as getRulesTier } from './subscription-rules';
import type { PlanFromPriceId } from './stripe-config';

export async function createCheckoutSession(priceId: string, mode: 'subscription' | 'payment') {
  const { data: { session } } = await supabase.auth.getSession();

  if (!session?.access_token) {
    throw new Error('No active session');
  }

  const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-checkout`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({
      price_id: priceId,
      success_url: `${window.location.origin}/subscription/success`,
      cancel_url: `${window.location.origin}/subscription`,
      mode,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create checkout session');
  }

  const { url } = await response.json();
  return url;
}

/**
 * VOY-607: Downgrade to Free by canceling the subscription at the end of the current
 * billing period. The user keeps paid benefits until then. Returns the updated
 * subscription fields from the backend.
 */
export async function cancelSubscriptionAtPeriodEnd(subscriptionId: string): Promise<SubscriptionActionResult> {
  return postSubscriptionAction(subscriptionId, 'cancel', 'Failed to cancel subscription');
}

/**
 * VOY-608: Reactivate a subscription. For a pending downgrade (still active, set to cancel at
 * period end) this keeps the current plan: same price, no new charge. Also used for canceled
 * or lapsed subscriptions from the profile.
 */
export async function reactivateSubscription(subscriptionId: string): Promise<SubscriptionActionResult> {
  return postSubscriptionAction(subscriptionId, 'reactivate', 'Failed to reactivate subscription');
}

/** Subscription fields returned by the backend's /cancel and /reactivate routes. */
export interface SubscriptionActionResult {
  id: string;
  status: string;
  cancel_at_period_end: boolean;
  current_period_end: number | null;
}

async function postSubscriptionAction(
  subscriptionId: string,
  action: 'cancel' | 'reactivate',
  fallbackError: string
): Promise<SubscriptionActionResult> {
  let response: Response;
  try {
    // The callers show the cleaned-up error inline, so skip apiFetch's raw-body toast.
    response = await apiFetch(`/api/subscriptions/${encodeURIComponent(subscriptionId)}/${action}`, {
      method: 'POST',
      suppressErrorToast: true,
    });
  } catch (err) {
    // apiFetch throws on any non-OK response with the raw body as the message,
    // e.g. '{"error":"Subscription cannot be canceled"}'. Surface just the error text.
    throw new Error(extractApiErrorMessage(err, fallbackError));
  }

  const data = await response.json().catch(() => null);
  if (!data?.subscription) {
    throw new Error(`Unexpected response from ${action} endpoint`);
  }
  return data.subscription;
}

function extractApiErrorMessage(err: unknown, fallback: string): string {
  const raw = err instanceof Error ? err.message : '';
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.error === 'string' && parsed.error) return parsed.error;
  } catch {
    // Not JSON (e.g. a network error) - fall through.
  }
  return raw && !raw.trim().startsWith('{') && !raw.trim().startsWith('<') ? raw : fallback;
}

/** Long en-US date used for renewal / period-end labels, e.g. "November 15, 2030". */
export function formatRenewalDate(date: Date): string {
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

/** Parse and format a subscription period end; null when missing or invalid. */
export function formatPeriodEnd(periodEnd: string | number | null | undefined): string | null {
  const date = parsePeriodEnd(periodEnd);
  return date ? formatRenewalDate(date) : null;
}

/**
 * Parse a subscription period end (Unix seconds/ms, numeric string, or ISO string).
 * Returns null for missing or implausible values.
 */
export function parsePeriodEnd(periodEnd: string | number | null | undefined): Date | null {
  if (!periodEnd) return null;
  try {
    let parsedDate: Date;
    if (typeof periodEnd === 'number' || /^\d+$/.test(String(periodEnd))) {
      const timestamp = typeof periodEnd === 'number' ? periodEnd : parseInt(periodEnd, 10);
      // If timestamp is in seconds (Stripe format), convert to milliseconds
      parsedDate = new Date(timestamp > 1000000000000 ? timestamp : timestamp * 1000);
    } else {
      // ISO string
      parsedDate = new Date(periodEnd);
    }

    // Validate date: must be a valid date and after 2000-01-01 (reasonable minimum)
    const minValidDate = new Date('2000-01-01');
    if (!isNaN(parsedDate.getTime()) && parsedDate > minValidDate) {
      return parsedDate;
    }
    console.warn('Invalid renewal date:', periodEnd, 'parsed as:', parsedDate);
  } catch (error) {
    console.error('Error parsing renewal date:', periodEnd, error);
  }
  return null;
}

/**
 * Outcome of a subscription lookup. `ok: false` means the plan is UNKNOWN (the query failed),
 * which callers must not confuse with `subscription: null` (the user has no subscription).
 */
export type SubscriptionFetchResult =
  | { ok: true; subscription: UserSubscription | null }
  | { ok: false; error: unknown };

// PostgREST answers for a request the view does not authorize (VOY-605 revoked anon on it).
const AUTH_ERROR_CODES = new Set(['42501', 'PGRST301', 'PGRST302']);

function isAuthError(error: { code?: string } | null, status?: number): boolean {
  return status === 401 || status === 403 || (!!error?.code && AUTH_ERROR_CODES.has(error.code));
}

/**
 * Fetch the current user's subscription, telling a failed lookup apart from "no subscription".
 *
 * VOY-610: a query that leaves before supabase-js attached the restored session goes out as
 * `anon` and fails with 42501. getSession() waits for the client's initialization, so after it
 * resolves the query is retried once with the session; with no session at all the visitor is
 * anonymous and has no subscription.
 *
 * @param expectedUserId - When given, the answer only counts if it was read with that user's
 *   session (the view filters by auth.uid() and returns no user id). A different or missing
 *   session — sign-in still settling, or an account switch mid-request — makes it a failure.
 */
export async function fetchCurrentSubscription(
  expectedUserId?: string | null
): Promise<SubscriptionFetchResult> {
  const query = () => supabase.from('stripe_user_subscriptions').select('*');
  const sessionUserId = async () => (await supabase.auth.getSession()).data.session?.user?.id ?? null;
  try {
    // One row per subscription: a user who cancelled and subscribed again has several, and
    // .maybeSingle() then errored (PGRST116), so a paying user was shown as Free.
    let { data, error, status } = await query();

    if (error && isAuthError(error, status)) {
      if (!(await sessionUserId())) {
        return expectedUserId
          ? { ok: false, error: new Error('No session yet for the signed-in user') }
          : { ok: true, subscription: null };
      }
      ({ data, error, status } = await query());
    }

    if (error) {
      console.error('Error fetching subscription:', error);
      return { ok: false, error };
    }

    if (expectedUserId !== undefined && (await sessionUserId()) !== expectedUserId) {
      return { ok: false, error: new Error('Subscription was read with another session') };
    }

    return { ok: true, subscription: pickCurrentSubscription((data ?? []) as UserSubscription[]) };
  } catch (error) {
    console.error('Error fetching subscription:', error);
    return { ok: false, error };
  }
}

/**
 * The current subscription, or null when there is none OR the lookup failed. Display-only
 * callers can use it; anything that downgrades a user must use fetchCurrentSubscription().
 */
export async function getCurrentSubscription(): Promise<UserSubscription | null> {
  const result = await fetchCurrentSubscription();
  return result.ok ? result.subscription : null;
}

/**
 * The subscription that represents the user's plan among all their rows: an active one if any
 * (the latest period end wins), otherwise the most recent one, so a cancelled plan still shows.
 */
export function pickCurrentSubscription(rows: UserSubscription[]): UserSubscription | null {
  if (!rows.length) return null
  const active = rows.filter((r) => isActiveSubscription(r))
  const pool = active.length ? active : rows
  // Milliseconds, whether the row holds Unix seconds (the view's bigint), milliseconds or an ISO
  // string; same rule as the renewal date below.
  const end = (r: UserSubscription) => {
    const v = r.current_period_end
    const t = typeof v === 'number' ? (v > 1e12 ? v : v * 1000) : Date.parse(v ?? '')
    return Number.isFinite(t) ? t : 0
  }
  return [...pool].sort((a, b) => end(b) - end(a))[0]
}

/**
 * Whether a subscription should be treated as the user's current/active plan: a paid status
 * (PAID_STATUSES in types/subscription: active, trialing). Returns false for null, missing status,
 * past_due (VOY-554: not paid) or any other status (e.g. `canceled`, `incomplete`, `not_started`).
 */
export function isActiveSubscription(subscription: UserSubscription | null): boolean {
  return isPaidStatus(subscription?.status);
}

/**
 * Get the tier ID from a subscription
 * Returns 'free' on null, error, or inactive subscription. VOY-554: the tier comes from
 * subscription_prices (tier_id on the view), through the one resolver in subscription-rules.
 */
export function getTierFromSubscription(subscription: UserSubscription | null): TierId {
  return getRulesTier(subscription).id;
}

/**
 * Format subscription data for display in UI components
 * Handles all subscription states including free tier
 * 
 * @param subscription - Raw subscription data from database (or null for free tier)
 * @param planInfo - Stripe plan info (or null if no subscription)
 * @returns Formatted subscription display data with tier information
 *
 * WARNING (VOY-554): a non-expired `past_due` subscription comes back with `isActive: true` and its
 * paid tier, which contradicts VOY-554 (paid = active or trialing only). Nothing in the app calls
 * this today (tests only); left for VOY-426. For tier or member pricing use `getTierFromSubscription`.
 */
export function formatSubscriptionForDisplay(
  subscription: UserSubscription | null,
  planInfo: PlanFromPriceId | null
): SubscriptionDisplayData {
  // Free tier - no subscription
  if (!subscription || !planInfo) {
    const freeTierConfig = getTierConfig('free');
    return {
      planName: 'Free Plan',
      planKey: null,
      tier: 'free',
      tierName: freeTierConfig.name,
      canHaveSpecialPrice: freeTierConfig.canHaveSpecialPrice,
      ticketLimit: freeTierConfig.ticketLimit,
      beneficiaryPolicy: freeTierConfig.beneficiaryPolicy,
      status: null,
      statusLabel: 'Free',
      statusColor: 'gray',
      renewalDate: null,
      renewalDateFormatted: null,
      price: 0,
      priceFormatted: '$0.00',
      benefits: freeTierConfig.features,
      isActive: false,
      isTrial: false,
      isCancelled: false,
      isFreeTier: true,
      cancelAtPeriodEnd: false,
      isAnnual: false,
    };
  }

  const { plan, tier, isAnnual } = planInfo;
  const product = STRIPE_PRODUCTS[plan];
  const tierConfig = getTierConfig(tier);
  const benefits = getPlanBenefits(plan);
  
  // Calculate price: if annual, show monthly equivalent
  const displayPrice = isAnnual 
    ? (product.annualPrice || product.price * 12 * 0.8) / 12 
    : product.price;
  
  // Parse dates and validate (filter out invalid dates like 1970-01-01)
  const renewalDate = parsePeriodEnd(subscription.current_period_end);

  const renewalDateFormatted = renewalDate ? formatRenewalDate(renewalDate) : null;

  // Check if subscription has expired based on renewal date
  const now = new Date();
  const hasExpired = renewalDate ? renewalDate < now : false;

  // Determine status label and color
  let statusLabel: string;
  let statusColor: 'green' | 'yellow' | 'red' | 'blue' | 'gray';
  let isActive = false;
  let isTrial = false;
  let isCancelled = false;

  // Handle null or undefined status
  const status = subscription.status || null;

  switch (status) {
    case 'active':
      // If renewal date is in the past, subscription has expired
      if (hasExpired) {
        statusLabel = 'Expired';
        statusColor = 'red';
        isActive = false;
        isCancelled = true;
      } else {
        statusLabel = subscription.cancel_at_period_end ? 'Cancelled' : 'Active';
        statusColor = subscription.cancel_at_period_end ? 'yellow' : 'green';
        isActive = true;
        isCancelled = subscription.cancel_at_period_end;
      }
      break;
    case 'trialing':
      // If trial has expired, show as expired
      if (hasExpired) {
        statusLabel = 'Expired';
        statusColor = 'red';
        isActive = false;
      } else {
        statusLabel = 'Trial';
        statusColor = 'blue';
        isActive = true;
        isTrial = true;
      }
      break;
    case 'canceled':
      statusLabel = renewalDate && renewalDate > now ? 'Cancelled' : 'Expired';
      statusColor = renewalDate && renewalDate > now ? 'yellow' : 'red';
      isCancelled = true;
      isActive = renewalDate ? renewalDate > now : false;
      break;
    case 'past_due':
      statusLabel = 'Past Due';
      statusColor = 'red';
      isActive = !hasExpired; // Still active if not expired
      break;
    case 'incomplete':
    case 'incomplete_expired':
      statusLabel = 'Payment Required';
      statusColor = 'red';
      isActive = false;
      break;
    case 'unpaid':
      statusLabel = 'Unpaid';
      statusColor = 'red';
      isActive = false;
      break;
    case null:
    case undefined:
      // If status is null/undefined, check renewal date
      if (hasExpired) {
        statusLabel = 'Expired';
        statusColor = 'red';
        isActive = false;
      } else {
        statusLabel = 'Active';
        statusColor = 'green';
        isActive = true;
      }
      break;
    default:
      // For any other unexpected status, check renewal date
      if (hasExpired) {
        statusLabel = 'Expired';
        statusColor = 'red';
        isActive = false;
      } else {
        console.warn('Unknown subscription status:', status, subscription);
        statusLabel = 'Active';
        statusColor = 'green';
        isActive = true;
      }
  }

  // If subscription is not active, treat as Free tier for pricing purposes
  const effectiveTierConfig = isActive ? tierConfig : getTierConfig('free');

  return {
    planName: product.name,
    planKey: plan,
    tier: isActive ? tier : 'free',
    tierName: effectiveTierConfig.name,
    canHaveSpecialPrice: isActive && effectiveTierConfig.canHaveSpecialPrice,
    ticketLimit: effectiveTierConfig.ticketLimit,
    beneficiaryPolicy: effectiveTierConfig.beneficiaryPolicy,
    status: subscription.status,
    statusLabel,
    statusColor,
    renewalDate,
    renewalDateFormatted,
    price: displayPrice,
    priceFormatted: `$${displayPrice.toFixed(2)}`,
    benefits: isActive ? benefits : getTierConfig('free').features,
    isActive,
    isTrial,
    isCancelled,
    isFreeTier: !isActive,
    cancelAtPeriodEnd: subscription.cancel_at_period_end,
    isAnnual,
  };
}

/**
 * Get subscription display data for the current user
 * Convenience function that fetches subscription and formats it
 */
export async function getCurrentSubscriptionDisplay(): Promise<SubscriptionDisplayData> {
  const subscription = await getCurrentSubscription();
  const planInfo = subscription
    ? getPlanFromPriceId(subscription.price_id, await loadSubscriptionPrices())
    : null;
  return formatSubscriptionForDisplay(subscription, planInfo);
}

// Unused or unreferenced check for delete
export async function getOrderHistory() {
  const { data, error } = await supabase
    .from('stripe_user_orders')
    .select('*')
    .order('order_date', { ascending: false });

  if (error) {
    console.error('Error fetching orders:', error);
    return [];
  }

  return data;
}
